import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, Subscription, throwError } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { IOrganizationBase } from '../models/organization';
import { UserService } from '../services/user/user.service';
import { ApplicationState } from './../state/app.state';
import { App } from './../state/app.actions';
import { isSafeReturnUrl } from './no-auth.guard';

@Injectable()
export class AddHeaderInterceptor
  implements OnDestroy, OnInit, HttpInterceptor
{
  token: string;

  @Select(ApplicationState.getToken)
  token$: Observable<string>;

  @Select(ApplicationState.getCurrentOrganization)
  currentOrganization$: Observable<IOrganizationBase>;

  unSubscribeAll$ = new Subject<void>();
  selectedOrganization: IOrganizationBase;

  subscription: Subscription;
  constructor(
    public store: Store,
    private userService: UserService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.token = this.store.snapshot().application.token;

    this.token$.pipe(
      takeUntil(this.unSubscribeAll$),
      tap((res) => {
        this.token = res;
      })
    );

    this.initOrganization();
  }

  initOrganization(): void {
    this.subscription = this.userService.currentOrganization$.subscribe({
      next: (organization) => {
        this.selectedOrganization = organization;
      },
      error: (err) => {
        console.log('error', err);
      },
    });
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.initOrganization();
    this.token$.pipe(
      takeUntil(this.unSubscribeAll$),
      tap((res) => {
        this.token = res;
      })
    );

    this.token = localStorage.getItem(`dt-token`);

    const headers: Record<string, string> = {
      Organization: this.selectedOrganization ? this.selectedOrganization.id : '',
    };
    // Only attach a Bearer header when a token actually exists — sending a
    // literal "Bearer null" string for logged-out requests serves no
    // purpose and only confuses the backend's Bearer-format check.
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const cloneRequest = req.clone({ setHeaders: headers });

    return next.handle(cloneRequest).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.handleAuthFailure(req);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Centralized reaction to a 401 from any API call: the backend's
   * token.service.js reserves 401 specifically for "not authenticated"
   * (missing/invalid/expired token), distinct from the 403s used elsewhere
   * for authenticated-but-forbidden business rules — so this never fires
   * for a plain permission error.
   */
  private handleAuthFailure(req: HttpRequest<any>): void {
    // The login and session-restore endpoints already own their own
    // 401 handling (bad credentials, or ApplicationState.initializeSession's
    // catchError) — don't pile a second logout/redirect on top of those.
    if (req.url.includes('/users/login') || req.url.includes('/users/session')) {
      return;
    }

    // ClearSession patches state synchronously, so once the first offending
    // response flips authStatus to 'unauthenticated', any other 401s
    // arriving moments later (e.g. several in-flight requests failing
    // together) see that here and bail out — only one logout/redirect ever
    // actually happens.
    if (this.store.snapshot().application.authStatus === 'unauthenticated') {
      return;
    }

    this.store.dispatch(new App.Actions.ClearSession());

    if (!this.router.url.startsWith('/authentication/page-login')) {
      const returnUrl = this.router.url;
      this.router.navigate(['/authentication/page-login'], {
        queryParams: isSafeReturnUrl(returnUrl) ? { returnUrl } : {},
      });
    }
  }

  ngOnDestroy(): void {
    this.unSubscribeAll$.next();
    this.unSubscribeAll$.complete();
    this.subscription.unsubscribe();
  }
}
